class CartDrawerInner extends HTMLElement {
  get content() {
    return this.querySelector(".side-panel-inner");
  }

  /**
   * Dims and locks the drawer for the duration of an update, holding its
   * current height so the swap cannot shift anything under the pointer.
   */
  beginUpdate() {
    const content = this.content;
    if (!content) return;

    content.style.minHeight = `${content.getBoundingClientRect().height}px`;
    this.setAttribute("data-updating", "true");
  }

  endUpdate() {
    const content = this.content;
    this.removeAttribute("data-updating");
    // Release the held height a frame later so the fresh markup is laid out
    // before the constraint disappears.
    requestAnimationFrame(() => {
      if (content) content.style.minHeight = "";
    });
  }

  async render() {
    const newHTML = await this.fetchSection();

    const parser = new DOMParser();
    const resAsHtml = parser.parseFromString(newHTML, "text/html");

    const updatedCart = resAsHtml.querySelector(".cart-drawer");
    if (!updatedCart) return;

    const currentContent = this.content;
    const newContent = updatedCart.querySelector(".side-panel-inner");
    if (!currentContent || !newContent) return;

    CartDrawerInner.keepLoadedRecommendations(currentContent, newContent);

    if (currentContent.innerHTML === newContent.innerHTML) return;

    currentContent.innerHTML = newContent.innerHTML;
  }

  /**
   * <product-recommendations> fills itself in from a second request, and a
   * plain section render comes back empty because `recommendations.performed`
   * is false. Dropping that empty version in would collapse a chunk of the
   * drawer until the refetch lands, so already loaded results are carried over.
   */
  static keepLoadedRecommendations(currentContent, newContent) {
    const current = currentContent.querySelectorAll("product-recommendations");
    const fresh = newContent.querySelectorAll("product-recommendations");

    fresh.forEach((target, index) => {
      const loaded = current[index];
      if (!loaded || target.children.length > 0) return;
      if (loaded.children.length === 0) return;

      target.innerHTML = loaded.innerHTML;
    });
  }

  async fetchSection() {
    const root =
      (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
    const res = await fetch(`${root}?section_id=cart-drawer`);
    return res.text();
  }
}

if (!customElements.get("cart-drawer-inner")) {
  customElements.define("cart-drawer-inner", CartDrawerInner);
}

/**
 * Discount code field of the cart drawer.
 *
 * Supports multiple codes at once: every apply sends the full list of codes
 * (existing + new) to `cart/update.js`, because Shopify replaces the whole set
 * on each update instead of merging.
 *
 * When a code does not stick, the reason is derived from the cart response:
 *
 *   duplicate     - the code is already on the cart (checked before any request)
 *   uncombinable  - the code works on its own, but not next to the codes that
 *                   are already applied (probed with a second request), or it
 *                   knocked one of the existing codes out of the cart
 *   invalid       - the code is not applicable, not even on an empty cart.
 *                   Shopify's storefront cart API reports expired, disabled,
 *                   used-up and unknown codes all as `applicable: false`, so
 *                   they share this message.
 *   empty         - nothing was typed
 *   generic       - the request itself failed
 *
 * The messages come from the theme settings and are passed in as data
 * attributes by `snippets/cart-drawer.liquid`.
 */
class CartDrawerDiscount extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.toggleBtn = this.querySelector(".drawer__discount-field-top");
    this.applyBtn = this.querySelector(".drawer__discount-field-apply");
    this.codeInput = this.querySelector(
      ".drawer__discount-field-bottom-top input",
    );
    this.errorEl = this.querySelector(".drawer__discount-field-bottom-error");

    this.toggleBtn.addEventListener("click", () => this.toggle());
    this.toggleBtn.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.toggle();
      }
    });

    this.applyBtn.addEventListener("click", () => this.applyDiscount());

    this.codeInput.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        this.applyDiscount();
      }
    });
    this.codeInput.addEventListener("input", () => this.clearError());

    // Delegated so that chips added by syncWithCart() work too.
    this.tilesEl = this.querySelector(".drawer__discount-field-tiles");
    this.tilesEl.addEventListener("click", (event) => {
      const button = event.target.closest(
        ".drawer__discount-field-tile-remove",
      );
      if (button) this.removeDiscount(button.dataset.discountCode);
    });

    this.syncWithCart();
  }

  /**
   * Liquid can only render the codes Shopify put into `cart.discount_applications`,
   * which leaves out codes that exclusively discount shipping - they are live on
   * the cart but invisible to the template. `cart.js` is the only place that lists
   * all of them, so the chips and the code list get topped up from there.
   */
  async syncWithCart() {
    if (this.dataset.cartEmpty === "true") return;

    try {
      const root =
        (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
      const res = await fetch(`${root}cart.js`);
      this.reconcileCodes(await res.json());
    } catch (error) {
      // Keep whatever Liquid rendered.
      console.error(error);
    }
  }

  reconcileCodes(cart) {
    const codes = CartDrawerDiscount.applicableCodes(cart);
    this.dataset.currentDiscountCodeList = codes.join(",");

    const rendered = new Set(
      [...this.tilesEl.querySelectorAll("[data-discount-code]")].map((tile) =>
        (tile.dataset.discountCode || "").toUpperCase(),
      ),
    );

    codes
      .filter((code) => !rendered.has(code.toUpperCase()))
      .forEach((code) => this.tilesEl.appendChild(this.buildTile(code)));
  }

  /** Chip for a code Liquid could not render, so without an amount. */
  buildTile(code) {
    const tile = document.createElement("p");
    tile.className = "drawer__discount-field-active-tile";
    tile.dataset.discountType = "discount_code";
    tile.dataset.discountCode = code;

    const label = document.createElement("span");
    label.className = "drawer__discount-field-active-tile-code";
    label.textContent = code;

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className =
      "drawer__discount-field-active-tile-remove drawer__discount-field-tile-remove";
    remove.dataset.discountCode = code;
    remove.dataset.ga4EventName = "remove_cart_discount";
    remove.setAttribute(
      "aria-label",
      (this.dataset.removeLabel || "[code]").replace("[code]", code),
    );
    remove.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M13 1L1 13M13 13L1 1" stroke="var(--color-body)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    tile.append(label, remove);
    return tile;
  }

  toggle() {
    const currentlyOpened = this.dataset.open === "true";
    this.setOpen(!currentlyOpened);

    if (!currentlyOpened) {
      this.codeInput.focus();
    }
  }

  setOpen(isOpen) {
    this.setAttribute("data-open", String(isOpen));
    this.toggleBtn.setAttribute("aria-expanded", String(isOpen));
  }

  /** Codes currently applied to the cart, as rendered by Liquid. */
  get appliedCodes() {
    return (this.dataset.currentDiscountCodeList || "")
      .split(",")
      .map((code) => code.trim())
      .filter(Boolean);
  }

  async applyDiscount() {
    if (this.applyBtn.dataset.state === "loading") return;

    const newCode = this.codeInput.value.trim();

    if (newCode === "") {
      this.showError("empty");
      return;
    }

    const appliedCodes = this.appliedCodes;

    if (CartDrawerDiscount.includesCode(appliedCodes, newCode)) {
      this.showError("duplicate");
      return;
    }

    this.clearError();
    this.setLoading(true);

    const drawer = document.querySelector("cart-drawer-inner");
    drawer.beginUpdate();

    let errorType = null;
    let finalCart = null;

    try {
      const cart = await this.setDiscountCodes([...appliedCodes, newCode]);
      finalCart = cart;

      const newCodeApplied = CartDrawerDiscount.isApplicable(cart, newCode);
      const previousCodesKept = appliedCodes.every((code) =>
        CartDrawerDiscount.isApplicable(cart, code),
      );

      if (!newCodeApplied) {
        errorType = "invalid";

        // The code may be perfectly valid but simply not combinable with what
        // is already in the cart - check it in isolation to tell both apart.
        if (appliedCodes.length > 0) {
          const probeCart = await this.setDiscountCodes([newCode]);
          if (CartDrawerDiscount.isApplicable(probeCart, newCode)) {
            errorType = "uncombinable";
          }
        }
      } else if (!previousCodesKept) {
        // The new code was accepted but pushed an existing one out.
        errorType = "uncombinable";
      }

      if (errorType) {
        finalCart = await this.setDiscountCodes(appliedCodes);
      }
    } catch (error) {
      console.error(error);
      errorType = "generic";
    }

    await drawer.render();

    // After calling render, there is a new discount element - unless the markup
    // did not change, in which case this element is still the live one.
    const cartDrawerDiscount = document.querySelector("cart-drawer-discount");
    // Always keep the discount field open
    cartDrawerDiscount.setOpen(true);
    cartDrawerDiscount.setLoading(false);
    if (finalCart) cartDrawerDiscount.reconcileCodes(finalCart);

    if (errorType) {
      cartDrawerDiscount.codeInput.value = newCode;
      cartDrawerDiscount.showError(errorType);

      localStorage.setItem(
        "sitecode_v2",
        JSON.stringify({
          code: newCode,
          savedAt: Date.now(),
        }),
      );
    }

    drawer.endUpdate();
  }

  /** Removes a single code and keeps every other code on the cart. */
  async removeDiscount(codeToRemove) {
    if (this.applyBtn.dataset.state === "loading") return;

    this.setLoading(true);

    const drawer = document.querySelector("cart-drawer-inner");
    drawer.beginUpdate();

    const wasOpen = this.dataset.open === "true";
    const remainingCodes = this.appliedCodes.filter(
      (code) => code.toUpperCase() !== (codeToRemove || "").toUpperCase(),
    );

    let errorType = null;
    let finalCart = null;

    try {
      finalCart = await this.setDiscountCodes(remainingCodes);
    } catch (error) {
      console.error(error);
      errorType = "generic";
    }

    await drawer.render();

    // After calling render, there is a new discount element
    const cartDrawerDiscount = document.querySelector("cart-drawer-discount");

    // Removing a code should not pop the input open on its own
    cartDrawerDiscount.setOpen(wasOpen);
    cartDrawerDiscount.setLoading(false);
    if (finalCart) cartDrawerDiscount.reconcileCodes(finalCart);

    if (errorType) {
      cartDrawerDiscount.showError(errorType);
    }

    drawer.endUpdate();
  }

  setLoading(isLoading) {
    this.applyBtn.dataset.state = isLoading ? "loading" : "idle";
  }

  showError(type) {
    const message =
      this.dataset[`error${type.charAt(0).toUpperCase()}${type.slice(1)}`] ||
      this.dataset.errorGeneric;

    this.setAttribute("data-state", "error");
    this.setOpen(true);
    this.errorEl.dataset.errorType = type;
    this.errorEl.textContent = message || "";
  }

  clearError() {
    if (this.dataset.state !== "error") return;

    this.setAttribute("data-state", "default");
    this.errorEl.textContent = "";
    delete this.errorEl.dataset.errorType;
  }

  /**
   * Shopify replaces the complete discount set on every update, so the full
   * list has to be sent each time. Returns the updated cart.
   *
   * A rejected code can come back as a 4xx error payload instead of a cart -
   * that is not an exception, it simply means the code did not stick, which
   * `isApplicable` reports back as `false`.
   */
  async setDiscountCodes(codes) {
    const res = await fetch(Shopify.routes.root + "cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        discount: codes.join(","),
      }),
    });

    if (res.status >= 500) {
      throw new Error(`cart/update.js responded with ${res.status}`);
    }

    return res.json();
  }

  /** Every code Shopify currently honours, shipping-only codes included. */
  static applicableCodes(cart) {
    if (!cart || !Array.isArray(cart.discount_codes)) return [];

    return cart.discount_codes
      .filter((discount) => discount.applicable === true)
      .map((discount) => discount.code)
      .filter(Boolean);
  }

  static includesCode(codes, code) {
    const needle = code.toUpperCase();
    return codes.some((current) => current.toUpperCase() === needle);
  }

  /** Whether the cart response actually granted the given code. */
  static isApplicable(cart, code) {
    if (!cart) return false;

    const needle = code.toUpperCase();

    if (Array.isArray(cart.discount_codes)) {
      const match = cart.discount_codes.find(
        (discount) => (discount.code || "").toUpperCase() === needle,
      );
      if (match) return match.applicable === true;
      return false;
    }

    // Older cart payloads only expose the applications.
    return (cart.discount_applications || []).some(
      (discount) => (discount.title || "").toUpperCase() === needle,
    );
  }

  getCurrentDiscountCodeList() {
    return this.appliedCodes.map((discount) => discount.toUpperCase());
  }
}

if (!customElements.get("cart-drawer-discount")) {
  customElements.define("cart-drawer-discount", CartDrawerDiscount);
}
